import { apiGet } from '../api/client';
import { getStoredLocale, isAppLocale, setAppLocale, type AppLocale } from '../i18n';
import { savePreferences, type PlayCurrency } from '../api/preferences';

const GEO_APPLIED_KEY = 'vbbplay.geo_defaults_applied';
const CURRENCY_MANUAL_KEY = 'vbbplay.play_currency_manual';

interface GeoSuggest {
  country: string | null;
  locale: string;
  play_currency: string;
  source: string;
}

function hasManualCurrency(): boolean {
  try {
    return localStorage.getItem(CURRENCY_MANUAL_KEY) === '1';
  } catch {
    return false;
  }
}

/** Apply country-based locale/currency once for first-visit users. Never overrides manual choices. */
export async function applyGeoDefaultsOnce(opts?: {
  loggedIn?: boolean;
  existingLocale?: string | null;
  existingPlayCurrency?: string | null;
}): Promise<void> {
  try {
    if (localStorage.getItem(GEO_APPLIED_KEY) === '1') return;
  } catch {
    // continue
  }

  let localeManual = false;
  try {
    localeManual = localStorage.getItem('vbbplay.locale_manual') === '1';
  } catch {
    localeManual = false;
  }
  const manualCurrency = hasManualCurrency();

  let suggest: GeoSuggest;
  try {
    suggest = await apiGet<GeoSuggest>('/api/v1/geo/suggest');
  } catch {
    return;
  }

  // Country suggestion wins once over browser detector for first visit; manual always wins.
  if (!localeManual && suggest.country && isAppLocale(suggest.locale)) {
    setAppLocale(suggest.locale as AppLocale);
  } else if (!getStoredLocale() && isAppLocale(suggest.locale)) {
    setAppLocale(suggest.locale as AppLocale);
  }

  const currency = suggest.play_currency as PlayCurrency;
  if (!manualCurrency && ['AZN', 'RUB', 'USD', 'EUR'].includes(currency)) {
    if (opts?.loggedIn) {
      try {
        await savePreferences({ play_currency: currency });
      } catch {
        // ignore preference write failures (e.g. open session)
      }
    } else {
      try {
        localStorage.setItem('vbbplay.suggested_play_currency', currency);
      } catch {
        // ignore
      }
    }
  }

  try {
    localStorage.setItem(GEO_APPLIED_KEY, '1');
  } catch {
    // ignore
  }
}

export function markPlayCurrencyManual(): void {
  try {
    localStorage.setItem(CURRENCY_MANUAL_KEY, '1');
  } catch {
    // ignore
  }
}
